import type { PropsWithChildren, ReactElement } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { PlainTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    ExpandButton,
    LetsGoButton,
    StyledOnboardingHubCard,
    StyledOnboardingHubCardBody,
    StyledOnboardingHubCardBodyWrapper,
    StyledOnboardingHubCardHeader,
    StyledOnboardingHubCardTitle,
} from "app/pages/sales-intelligence/sub-modules/onboarding/sidebar-hub/components/OnboardingHubCard/styles";

interface Props {
    isExpanded: boolean;
    isDone: boolean;
    title: string;
    onExpand: (isExpand: boolean) => void;
    onNavigate?: () => void;
}

const OnboardingHubCard = ({
    title,
    isDone,
    isExpanded,
    onExpand,
    onNavigate,
    children,
}: PropsWithChildren<Props>): ReactElement => {
    return (
        <StyledOnboardingHubCard
            isExpanded={isExpanded}
            isDone={isDone}
            data-automation="onboarding-hub-task-card"
        >
            <StyledOnboardingHubCardHeader>
                {isDone ? (
                    <SWReactIcons
                        className="doneMarkIcon"
                        iconName="checked-circle"
                        iconColor={colorsPalettes.mint[400]}
                        size="xs"
                    />
                ) : (
                    <PlainTooltip
                        key="tooltip-expand-button"
                        placement="top"
                        enabled={!isExpanded}
                        tooltipContent={translate("si.onboarding.tooltip.expand-button.label")}
                    >
                        <div>
                            <ExpandButton
                                isExpanded={isExpanded}
                                type="flat"
                                iconName="chev-right"
                                iconSize="xs"
                                onClick={() => onExpand(!isExpanded)}
                            />
                        </div>
                    </PlainTooltip>
                )}

                <StyledOnboardingHubCardTitle isDone={isDone}>{title}</StyledOnboardingHubCardTitle>

                {!isDone && !isExpanded ? (
                    <PlainTooltip
                        key="lets-go-button"
                        placement="top"
                        tooltipContent={translate("si.onboarding.tooltip.lets-go-button.label")}
                    >
                        <div>
                            <LetsGoButton
                                onClick={onNavigate}
                                type="flat"
                                iconName="arrow-right"
                                iconSize="xs"
                                isExpanded={isExpanded}
                                isInSidebar={false}
                            />
                        </div>
                    </PlainTooltip>
                ) : null}
            </StyledOnboardingHubCardHeader>

            <StyledOnboardingHubCardBodyWrapper isExpanded={isExpanded}>
                <div>
                    <StyledOnboardingHubCardBody data-automation="onboarding-hub-task-card.content">
                        {children}
                    </StyledOnboardingHubCardBody>
                </div>
            </StyledOnboardingHubCardBodyWrapper>
        </StyledOnboardingHubCard>
    );
};

export default OnboardingHubCard;
